import { useParams, Link, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/hooks/useLanguage";
import { SITE_ORIGIN } from "@/data/site";
import { MAIN_SITE_URL } from "@/data/categories";
import { pillarArticles } from "@/data/articles";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import JsonLd from "@/components/feature/JsonLd";

const PUBLISHED = "2026-01-20";
const UPDATED = "2026-08-19";

export default function Pillar() {
  const { t } = useTranslation();
  const { currentLang } = useLanguage();
  const { slug } = useParams();

  const article = pillarArticles.find((a) => a.slug === slug);

  if (!article) {
    return <Navigate to={`/${currentLang}`} replace />;
  }

  const title = article.title[currentLang];
  const summary = article.summary[currentLang];
  const url = `${SITE_ORIGIN}/${currentLang}/article/${article.slug}`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: title,
    description: summary,
    author: { "@type": "Organization", name: t("author") },
    publisher: { "@type": "Organization", name: t("brand.name") },
    datePublished: PUBLISHED,
    dateModified: UPDATED,
    mainEntityOfPage: url,
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: article.faq.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: t("nav.home"),
        item: `${SITE_ORIGIN}/${currentLang}`,
      },
      {
        "@type": "ListItem",
        position: 2,
        name: title,
        item: url,
      },
    ],
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd id="ld-article" data={JSON.stringify(articleSchema)} />
      <JsonLd id="ld-faq" data={JSON.stringify(faqSchema)} />
      <JsonLd id="ld-breadcrumb" data={JSON.stringify(breadcrumbSchema)} />

      <Navbar variant="solid" />

      <header className="bg-background-100 pt-24 pb-10 md:pt-28 md:pb-14">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500">
            <Link to={`/${currentLang}`} className="transition hover:text-primary-700">
              {t("nav.home")}
            </Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="truncate text-foreground-700">{t("nav.articles")}</span>
          </nav>

          <h1 className="mt-6 text-2xl font-semibold leading-tight text-foreground-950 md:text-4xl">
            {title}
          </h1>

          <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
            {summary}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground-500">
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-user-line"></i>
              {t("author")}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-calendar-line"></i>
              {t("published")}: {PUBLISHED}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <i className="ri-refresh-line"></i>
              {t("updated")}: {UPDATED}
            </span>
          </div>
        </div>
      </header>

      <article className="mx-auto max-w-3xl px-4 py-12 md:px-6 md:py-16">
        <p className="text-base leading-relaxed text-foreground-700">{article.intro}</p>

        {article.sections.map((section) => (
          <section key={section.heading} className="mt-12">
            <h2 className="text-xl font-semibold text-foreground-950 md:text-2xl">
              {section.heading}
            </h2>
            {section.paragraphs.map((p, i) => (
              <p
                key={i}
                className="mt-4 text-sm leading-relaxed text-foreground-700 md:text-base"
              >
                {p}
              </p>
            ))}
          </section>
        ))}

        <section className="mt-12">
          <h2 className="text-xl font-semibold text-foreground-950 md:text-2xl">
            {t("artfaq.title")}
          </h2>
          <div className="mt-5 space-y-4">
            {article.faq.map((f) => (
              <details
                key={f.q}
                className="group rounded-lg border border-background-200 bg-background-50 p-5"
              >
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 text-sm font-semibold text-foreground-900 md:text-base">
                  {f.q}
                  <i className="ri-add-line text-lg text-primary-600 transition group-open:rotate-45"></i>
                </summary>
                <p className="mt-4 text-sm leading-relaxed text-foreground-700">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="mt-12 rounded-lg bg-primary-50 p-8 text-center">
          <h2 className="text-xl font-semibold text-foreground-950 md:text-2xl">
            {t("cta.title")}
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-foreground-700">
            {t("cta.text")}
          </p>
          <a
            href={MAIN_SITE_URL}
            target="_blank"
            rel="noopener nofollow"
            className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary-500 px-7 py-3.5 text-sm font-semibold text-background-50 whitespace-nowrap cursor-pointer transition hover:bg-primary-600"
          >
            {t("cta.button")}
            <i className="ri-arrow-right-up-line"></i>
          </a>
        </section>

        <section className="mt-12 rounded-lg border border-background-200 bg-background-100 p-6">
          <h2 className="text-base font-semibold text-foreground-950">
            {t("related.title")}
          </h2>
          <p className="mt-2 text-sm text-foreground-600">{t("related.text")}</p>
          <div className="mt-4 flex flex-wrap gap-3">
            <Link
              to={`/${currentLang}`}
              className="inline-flex items-center gap-2 rounded-full bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-900 whitespace-nowrap cursor-pointer transition hover:bg-primary-500 hover:text-background-50"
            >
              <i className="ri-arrow-left-line"></i>
              {t("cta.backHome")}
            </Link>
            <Link
              to={`/${currentLang}#categories`}
              className="inline-flex items-center gap-2 rounded-full bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-900 whitespace-nowrap cursor-pointer transition hover:bg-primary-500 hover:text-background-50"
            >
              {t("categories.title")}
            </Link>
          </div>
        </section>

        <p className="mt-8 text-xs leading-relaxed text-foreground-500">
          {t("disclaimer")}
        </p>
      </article>

      <Footer />
    </div>
  );
}